import { alpha, createTheme, Theme } from '@mui/material/styles';

import type {} from '@mui/lab/themeAugmentation';

type StyleOverrides<ClassKey extends string = string, T = Theme> = Partial<Record<
  ClassKey,
  object | ((props: { theme: T } & Record<string, unknown>) => object)
>>;

type GratiScorecardClassKey = 'root' | 'row' | 'rowlabel' | 'cell' | 'header' | 'title' | 'total';

export const APP_BAR_HEIGHT = 8;
export const TAB_BAR_HEIGHT = 6;

/** Very small screens (iPhone SE and narrower): a FAB shows its icon only, no label. */
export const COMPACT_FAB_QUERY = '@media (max-width: 23.99rem)';

/** Sticky top offset for elements positioned directly below the AppBar. Responsive to account for
 *  MUI Toolbar minHeight differences and the Toolbar's 1px border-bottom. */
// AppBar height per breakpoint (matches theme.mixins.toolbar: 56px / 64px at 16px root font)
export const APPBAR_HEIGHT_REM = { xs: 3.5, sm: 4 };
export const APPBAR_STICKY_TOP = { xs: `${APPBAR_HEIGHT_REM.xs}rem`, sm: `calc(${APPBAR_HEIGHT_REM.sm}rem + 1px)` };

// Construction icon SVG path (from @mui/icons-material/Construction)
const CONSTRUCTION_ICON_PATH =
  'm13.78 15.17 2.12-2.12 6 5.99-2.12 2.12zM17.5 10c1.93 0 3.5-1.57 3.5-3.5 0-.58-.16-1.12-.41-1.6l-2.7 2.7-1.49-1.49 2.7-2.7c-.48-.25-1.02-.41-1.6-.41C15.57 3 14 4.57 14 6.5c0 .41.08.8.21 1.16l-1.85 1.85-1.78-1.78.71-.71-1.41-1.41L12 3.49c-1.17-1.17-3.07-1.17-4.24 0L4.22 7.03l1.41 1.41H2.81l-.71.71 3.54 3.54.71-.71V9.15l1.41 1.41.71-.71 1.78 1.78-7.41 7.41 2.12 2.12L16.34 9.79c.36.13.75.21 1.16.21';

// Repeating tile with embossed Construction icon (shadow + highlight offset)
const EMULATOR_WATERMARK_SVG = [
  `<svg xmlns='http://www.w3.org/2000/svg' width='64' height='64' viewBox='0 0 64 64'>`,
  `<path d='${CONSTRUCTION_ICON_PATH}' fill='rgba(0,0,0,0.15)' transform='translate(21,21) rotate(-20 12 12)'/>`,
  `<path d='${CONSTRUCTION_ICON_PATH}' fill='rgba(255,255,255,0.07)' transform='translate(20,20) rotate(-20 12 12)'/>`,
  `</svg>`,
].join('');
const EMULATOR_WATERMARK_BG = `url("data:image/svg+xml,${EMULATOR_WATERMARK_SVG.replace(/</g, '%3C').replace(/>/g, '%3E').replace(/#/g, '%23')}")`;

declare module '@mui/material/styles' {
  interface Components {
    GratiCard?: {
      variants?: {
        props: {
          variant: 'elevation' | 'outlined' | 'test' | 'none';
        };
        style: React.CSSProperties;
      }[];
      styleOverrides?: StyleOverrides;
    };
    GratiStateBox?: {
      styleOverrides?: StyleOverrides;
    };
    GratiScorecard?: {
      styleOverrides?: StyleOverrides<GratiScorecardClassKey>;
    };
    GratiColorPicker?: {
      styleOverrides?: StyleOverrides;
    };
    GratiDirectoryItem?: {
      styleOverrides?: StyleOverrides;
    };
    GratiDirectory?: {
      styleOverrides?: StyleOverrides;
    };
    GratiDirectorySubheader?: {
      styleOverrides?: StyleOverrides;
    };
    GratiTabBar?: {
      styleOverrides?: StyleOverrides;
    };
    GratiActionBar?: {
      styleOverrides?: StyleOverrides;
    };
    GratiActionBarContainer?: {
      styleOverrides?: StyleOverrides;
    };
    GratiSection?: {
      styleOverrides?: StyleOverrides;
    };
    GratiHourglass?: {
      styleOverrides?: StyleOverrides;
    };
    EventResultEntrySummary?: {
      styleOverrides?: StyleOverrides<'viewer'>;
    };
    GratiEditFab?: {
      styleOverrides?: StyleOverrides<'root' | 'icons'>;
    };
    GratiIconSwitch?: {
      styleOverrides?: StyleOverrides<'root' | 'thumb' | 'thumbText'>;
    };
    GratiPublishSwitch?: {
      styleOverrides?: StyleOverrides<'root'>;
    };
    GratiKey?: {
      styleOverrides?: StyleOverrides<'root' | 'clip' | 'content' | 'group' | 'title' | 'items' | 'item' | 'detail' | 'note' | 'swatch' | 'toggle'>;
    };
    GratiResultTable?: {
      styleOverrides?: StyleOverrides;
    };
    GratiScoreTable?: {
      styleOverrides?: StyleOverrides;
    };
    GratiScoreEntry?: {
      styleOverrides?: StyleOverrides;
    };
    GratiMarker?: {
      defaultProps?: {
        shape?: 'circle' | 'square' | 'shield';
        size?: string;
        faded?: boolean;
      };
    };
    GratiMarkerEditor?: {
      defaultProps?: {
        shape?: 'circle' | 'square' | 'shield';
        size?: string;
        disabled?: boolean;
      };
    };
  }
  interface Palette {
    tertiary: Palette['primary'];
    god: Palette['primary'];
  }

  interface PaletteOptions {
    inactive?: PaletteOptions['primary'];
    unpublished?: PaletteOptions['primary'];
    tertiary?: PaletteOptions['primary'];
    surface?: PaletteOptions['primary'];
    outline?: PaletteOptions['primary'];
    test?: PaletteOptions['primary'];
    emulator?: PaletteOptions['primary'];
    god?: PaletteOptions['primary'];
    caution?: PaletteOptions['primary'];
  }

  interface PaletteColor {
    container?: string;
    onContainer?: string;
    outline?: string;
    outlineVariant?: string;
    dim?: string;
    tint?: string;
    variant?: string;
    onVariant?: string;
    brand?: string;
    onBrand?: string;
    bright?: string;
    containerLowest?: string;
    containerLow?: string;
    containerHigh?: string;
    containerHighest?: string;
  }

  interface SimplePaletteColorOptions {
    container?: string;
    onContainer?: string;
    outline?: string;
    outlineVariant?: string;
    fixed?: string;
    onFixed?: string;
    fixedDim?: string;
    onFixedVariant?: string;
    inverse?: string;
    onInverse?: string;
    dim?: string;
    tint?: string;
    variant?: string;
    onVariant?: string;
    brand?: string;
    onBrand?: string;
    bright?: string;
    containerLowest?: string;
    containerLow?: string;
    containerHigh?: string;
    containerHighest?: string;
  }

  interface PaletteBackgroundChannel {
    default: string;
    on?: string;
  }

  // headline (h5) - top of the page
  // title (h6) - page section titles
  // h6dense (h6) - (dense variant)
  // pdense (p) - (dense variant)
  // iconbutton (body1) - navigation button label

  interface TypographyVariants {
    headline: React.CSSProperties;
    title: React.CSSProperties;
    h6dense: React.CSSProperties;
    label: React.CSSProperties;
    pdense: React.CSSProperties;
    iconbutton: React.CSSProperties;
    displayLarge: React.CSSProperties;
    displayMedium: React.CSSProperties;
    displaySmall: React.CSSProperties;
    headlineLarge: React.CSSProperties;
    headlineMedium: React.CSSProperties;
    headlineSmall: React.CSSProperties;
    titleLarge: React.CSSProperties;
    titleMedium: React.CSSProperties;
    titleSmall: React.CSSProperties;
    brandSmall: React.CSSProperties;
    brandMedium: React.CSSProperties;
    brandLarge: React.CSSProperties;
    labelLarge: React.CSSProperties;
    labelMedium: React.CSSProperties;
    labelSmall: React.CSSProperties;
    bodyLarge: React.CSSProperties;
    bodyMedium: React.CSSProperties;
    bodySmall: React.CSSProperties;
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    headline?: React.CSSProperties;
    title?: React.CSSProperties;
    h6dense?: React.CSSProperties;
    label?: React.CSSProperties;
    pdense?: React.CSSProperties;
    iconbutton?: React.CSSProperties;
    displayLarge?: React.CSSProperties;
    displayMedium?: React.CSSProperties;
    displaySmall?: React.CSSProperties;
    headlineLarge?: React.CSSProperties;
    headlineMedium?: React.CSSProperties;
    headlineSmall?: React.CSSProperties;
    titleLarge?: React.CSSProperties;
    titleMedium?: React.CSSProperties;
    titleSmall?: React.CSSProperties;
    brandSmall?: React.CSSProperties;
    brandMedium?: React.CSSProperties;
    brandLarge?: React.CSSProperties;
    labelLarge?: React.CSSProperties;
    labelMedium?: React.CSSProperties;
    labelSmall?: React.CSSProperties;
    bodyLarge?: React.CSSProperties;
    bodyMedium?: React.CSSProperties;
    bodySmall?: React.CSSProperties;
  }
}

// Update the Typography's variant prop options
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    headline: true;
    title: true;
    h6dense: true;
    label: true;
    pdense: true;
    iconbutton: true;
    displayLarge: true;
    displayMedium: true;
    displaySmall: true;
    headlineLarge: true;
    headlineMedium: true;
    headlineSmall: true;
    titleLarge: true;
    titleMedium: true;
    titleSmall: true;
    brandSmall: true;
    brandMedium: true;
    brandLarge: true;
    labelLarge: true;
    labelMedium: true;
    labelSmall: true;
    bodyLarge: true;
    bodyMedium: true;
    bodySmall: true;
  }
}

const iPhoneInput = {
  styleOverrides: {
    root: {
      '*': {
        userSelect: 'text !important' /* Standard syntax */,
      },
    },
  },
};

const mui6Theme = createTheme({
  components: {
    MuiChip: {
      styleOverrides: {
        root: {
          height: '2.25rem',
        },
        // Compact badge-style chip for inline use (e.g. division labels on tee sheet)
        sizeSmall: {
          height: '1.5em',
          '& .MuiChip-label': {
            paddingLeft: '0.4em',
            paddingRight: '0.4em',
            fontSize: '0.75em',
          },
        },
      },
    },
    MuiTextField: iPhoneInput,
    MuiInput: iPhoneInput,
    MuiInputBase: iPhoneInput,
    MuiFilledInput: iPhoneInput,
    MuiOutlinedInput: iPhoneInput,
    GratiCard: {
      styleOverrides: {
        root: ({ theme, ownerState }: { theme: Theme; ownerState?: { variant?: string } }) => {
          const baseStyles = theme.unstable_sx({
            my: 1,
            minWidth: '22.375rem',
            maxWidth: '37.25rem',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flexStart',
            containerType: 'inline-size',
          });

          // Variant-specific styles
          const variantStyles = (() => {
            switch (ownerState?.variant) {
              case 'elevation':
                return theme.unstable_sx({
                  border: 'none',
                  backgroundColor: 'surface.main',
                  color: 'surface.contrastText',
                });
              case 'outlined':
                return theme.unstable_sx({
                  borderRadius: '1rem',
                  border: '.1rem solid',
                  borderColor: 'outline.main',
                  backgroundColor: 'surface.main',
                  color: 'surface.contrastText',
                });
              case 'test':
                return theme.unstable_sx({
                  borderRadius: '1rem',
                  border: '.2rem dotted',
                  borderColor: 'test.main',
                  backgroundColor: 'surface.variant',
                  color: 'surface.onVariant',
                });
              case 'none':
                return theme.unstable_sx({
                  borderRadius: 0,
                  border: 'none',
                  backgroundColor: 'surface.main',
                  color: 'surface.contrastText',
                });
              default:
                return {};
            }
          })();

          return { ...baseStyles, ...variantStyles };
        },
        colorbar: ({ theme }) =>
          theme.unstable_sx({
            height: '0.375rem',
            alignSelf: 'stretch',
            backgroundColor: 'secondary.main'
          }),
        header: ({ theme }) =>
          theme.unstable_sx({
            height: 'auto',
            display: 'flex',
            pb: '0.75rem',
            pt: '0.75rem',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'flexStart',
            gap: '0.75rem',
            alignSelf: 'stretch',
            backgroundColor: 'surface.container',
            color: 'surface.onContainer'
          }),
        headercontent: ({ theme }) =>
          theme.unstable_sx({
            display: 'flex',
            pt: '0.5rem',
            pb: '0.5rem',
            pr: '0.5rem',
            pl: '1rem',
            alignItems: 'center',
            alignSelf: 'stretch',
            gap: '0.8125rem',
          }),
        avatar: ({ theme }) =>
          theme.unstable_sx({
            width: '3.125rem',
            height: '3.125rem',
            aspectRatio: '1/1',
            flexShrink: 0,
            display: { xs: 'none', sm: 'block' },
            '@container (max-width: 400px)': {
              display: 'none',
            },
            '& > img': {
              m: 0,
              backgroundColor: 'primary.container',
              color: 'primary.onContainer',
            },
          }),
        context: ({ theme }) =>
          theme.unstable_sx({
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flexStart',
            gap: '0.75rem',
            flex: '1 1 auto',
            flexGrow: 1,
            minWidth: 0,
          }),
        category: ({ theme }) =>
          theme.unstable_sx({
            fontSize: '0.875rem',
            fontStyle: 'normal',
            fontWeight: 500,
            lineHeight: 'normal',
            height: '1.063rem',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }),
        state: ({ theme }) =>
          theme.unstable_sx({
            display: 'inline-flex',
            height: '1rem',
            py: '0.75rem',
            px: '1rem',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '0.625rem',
            borderRadius: '6.25rem',
            border: '1px solid',
            borderColor: 'outline.main',
            backgroundColor: 'surface.main',
            fontSize: '1rem', 
            fontStyle: 'normal',
            fontWeight: 500,
            lineHeight: 'normal',
            textTransform: 'uppercase',
            alignSelf: 'flex-start',
            maxWidth: 'calc(100% - 2rem - 2px)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }),
        spotlight: ({ theme }) =>
          theme.unstable_sx({
            display: 'flex',
            width: '3.19rem',
            height: '3.88rem',
            px: '1rem',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '0.625rem',
            flexShrink: 0,
            borderRadius: '1rem',
            border: '.1rem solid',
            borderColor: 'outline.main',
            backgroundColor: 'surface.main',
          }),
        topaction: ({ theme }) =>
          theme.unstable_sx({
            flexShrink: 0,
          }),
        alert: ({ theme }) =>
          theme.unstable_sx({
            display: 'flex',
            py: '1rem',
            px: '4.375rem',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '0.625rem',
            alignSelf: 'stretch',
            backgroundColor: 'primary.container',
            color: 'primary.onContainer',
            fontSize: '0.875rem',
            fontStyle: 'normal',
            fontWeight: 500,
            lineHeight: 'normal',
          }),
        body: ({ theme }) =>
          theme.unstable_sx({
            display: 'flex',
            px: '.25rem',
            pt: '.5rem',
            pb: '.5rem',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'flex-start',
            gap: '1.5rem',
            alignSelf: 'stretch',
          }),
        title: ({ theme }) =>
          theme.unstable_sx({
            fontSize: '1.5rem',
            fontStyle: 'normal',
            fontWeight: 500,
            lineHeight: 'normal',
          }),
        subtitle: ({ theme }) =>
          theme.unstable_sx({
            pt: '0.5rem',
            fontSize: '0.875rem',
            fontStyle: 'normal',
            fontWeight: 400,
            lineHeight: 'normal',
          }),
        description: ({ theme }) =>
          theme.unstable_sx({
            fontSize: '0.75rem',
            fontStyle: 'normal',
            fontWeight: 400,
            lineHeight: 'normal',
            color: 'surface.contrastText',
          }),
        details: ({ theme }) =>
          theme.unstable_sx({
            pb: '1.5rem'
          }),
        detail: ({ theme }) =>
          theme.unstable_sx({
            pt: '0.5rem',
            '& .MuiSvgIcon-root': {
              opacity: 0.75,
            },
            '& .preText, & .postText, & .status': {
              opacity: 0.75,
            },
          }),
        actionbar: ({ theme }) =>
          theme.unstable_sx({
            '& .MuiTypography-bodyLarge': {
              typography: 'bodyLarge',
              mb: 1,
              color: 'surface.onVariant',
            },
            '& .MuiTypography-bodyMedium': {
              color: 'surface.contrastText',
            },
            flexDirection: 'row',
            flexGrow: 1,
            justifyContent: 'flex-end',
          }),
        collapsedname: ({ theme }) =>
          theme.unstable_sx({
            px: 1.5,
            py: 1.25,
            overflow: 'hidden',
          }),
        expandaffordance: ({ theme }) =>
          theme.unstable_sx({
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5,
            py: 0.75,
            cursor: 'pointer',
            backgroundColor: 'surface.container',
            color: 'text.secondary',
            opacity: 0.6,
            '&:hover': { opacity: 1, filter: 'brightness(0.96)' },
          }),
      },
    },
    GratiStateBox: {
      styleOverrides: {
        root: ({ theme, ownerState }: { theme: Theme; ownerState?: { variant?: string } }) => {
          const baseStyles = theme.unstable_sx({
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5,
            px: 2,
            py: 0.75,
            borderRadius: '6.25rem',
            fontSize: '1rem',
            fontStyle: 'normal',
            fontWeight: 600,
            lineHeight: '1.25rem',
            letterSpacing: '0.00625rem',
            '& .MuiTypography-root, & .MuiSvgIcon-root': {
              color: 'inherit',
            },
          });

          // Variant-specific styles
          const variantStyles = (() => {
            switch (ownerState?.variant) {
              case 'outlined':
                return theme.unstable_sx({
                  border: '1px solid',
                  borderColor: 'transparent',
                  backgroundColor: 'success.main',
                  color: 'success.contrastText',
                });
              case 'filled':
                return theme.unstable_sx({
                  border: '1px solid',
                  borderColor: 'transparent',
                  backgroundColor: 'success.main',
                  color: 'success.contrastText',
                });
              case 'text':
                return theme.unstable_sx({
                  border: 'none',
                  backgroundColor: 'transparent',
                  color: 'surface.contrastText',
                  px: 1,
                });
              default:
                return {};
            }
          })();

          return { ...baseStyles, ...variantStyles };
        },
      },
    },
    GratiHourglass: {
      styleOverrides: {
        root: ({ theme, ownerState }: { theme: Theme; ownerState?: { variant?: string } }) => {
          const baseStyles = theme.unstable_sx({
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          });

          const variantStyles = (() => {
            switch (ownerState?.variant) {
              case 'inline':
                return theme.unstable_sx({
                  '& .GratiHourglass-progress': { width: '1.5rem !important', height: '1.5rem !important' },
                  '& .GratiHourglass-icon': { fontSize: '0.75rem' },
                });
              case 'page':
              default:
                return theme.unstable_sx({
                  margin: 3,
                  '& .GratiHourglass-progress': { width: '3rem !important', height: '3rem !important' },
                  '& .GratiHourglass-icon': { fontSize: '1.5rem' },
                });
            }
          })();

          return { ...baseStyles, ...variantStyles };
        },
      },
    },
    GratiScorecard: {
      styleOverrides: {
        root: ({ theme }) =>
          theme.unstable_sx({
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'flex-start',
            flexGrow: 0,
            width: '100%',
            padding: '0rem',
            overflowX: 'auto',
            overflowY: 'auto',
            overflowWrap: 'break-word',
            backgroundColor: 'surface.main',
            color: 'surface.contrastText',
            fontSize: '.8rem', // Adjust as needed
            fontFamily: 'Inter',
          }),
        row: ({ theme }) =>
          theme.unstable_sx({
            flexDirection: 'row',
            flexGrow: 0,
            mb: '.25rem',
          }),
        rowlabel: ({ theme }) =>
          theme.unstable_sx({
            display: 'flex',
            flexShrink: 0,
            justifyContent: 'left',
            alignItems: 'center',
            textAlign: 'left',
            width: { xs: '5rem', sm: '7rem', md: '9rem' },
            padding: '0rem',
            pl: '.5rem',
            typography: {
              textAlign: 'left',
            },
            fontSize: '.8rem', // Adjust as needed
            fontFamily: 'Inter',
          }),
        cell: ({ theme }) =>
          theme.unstable_sx({
            display: 'flex',
            flexShrink: 0,
            flexGrow: 1,
            justifyContent: 'center',
            alignItems: 'center',
            padding: '0rem',
            width: { xs: '1.3rem', md: '1.4rem' },
            height: { xs: '1.3rem', md: '1.4rem' },
            my: '.1rem',
            py: '.1rem',
            fontSize: '.8rem', // Adjust as needed
            fontFamily: 'Inter',
          }),
        header: ({ theme }) =>
          theme.unstable_sx({
            display: 'flex',
            flexDirection: 'row',
            flexGrow: 0,
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            class: 'TableStack',
            mt: '.5rem',
            mb: '.25rem',
            padding: '0rem',
            backgroundColor: 'surface.variant',
            color: 'surface.onVariant',
            fontSize: '.8rem', // Adjust as needed
            fontFamily: 'Inter',
          }),
        title: ({ theme }) =>
          theme.unstable_sx({
            mt: '.5rem',
            color: 'surface.contrastText',
            fontFamily: 'Inter',
            fontSize: '1.1rem',
            fontWeight: 300,
          }),
        total: ({ theme }) =>
          theme.unstable_sx({
            justifyContent: 'flex-start',
            textAlign: 'left',
            pl: '.5rem',
            width: { xs: '3rem', sm: '4rem', md: '5rem' },
          }),
      },
    },
    GratiResultTable: {
      styleOverrides: {
        root: {
          borderCollapse: 'collapse',
          width: '100%',
        },
        headerCell: ({ theme }: { theme: Theme }) => ({
          fontWeight: 'bold',
          whiteSpace: 'nowrap',
          fontSize: '0.8rem',
          padding: '0.2rem',
          borderBottom: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.action.hover,
          [theme.breakpoints.down('md')]: { fontSize: '0.75rem', padding: '0.15rem' },
          [theme.breakpoints.down('sm')]: { fontSize: '0.75rem' },
        }),
        posHeaderCell: {
          textAlign: 'left',
        },
        nameHeaderCell: ({ theme }: { theme: Theme }) => ({
          textAlign: 'left',
          minWidth: '7rem',
          padding: '0.2rem 0.5rem 0.2rem 0.4rem',
          [theme.breakpoints.down('md')]: { minWidth: '6rem', padding: '0.15rem 0.4rem 0.15rem 0.35rem' },
          [theme.breakpoints.down('sm')]: { minWidth: '5rem', padding: '0.15rem 0.3rem 0.15rem 0.3rem' },
        }),
        scoreHeaderCell: {
          textAlign: 'right',
        },
        row: ({ theme }: { theme: Theme }) => ({
          '&:nth-of-type(even)': {
            backgroundColor: theme.palette.action.hover,
          },
        }),
        posCell: ({ theme }: { theme: Theme }) => ({
          textAlign: 'left',
          whiteSpace: 'nowrap',
          fontSize: '0.8rem',
          padding: '0.2rem',
          [theme.breakpoints.down('md')]: { fontSize: '0.75rem', padding: '0.15rem' },
          [theme.breakpoints.down('sm')]: { fontSize: '0.75rem' },
        }),
        nameCell: ({ theme }: { theme: Theme }) => ({
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          fontSize: '0.8rem',
          minWidth: '7rem',
          padding: '0.2rem 0.5rem 0.2rem 0.4rem',
          [theme.breakpoints.down('md')]: {
            minWidth: '6rem',
            padding: '0.15rem 0.4rem 0.15rem 0.35rem',
            fontSize: '0.75rem',
          },
          [theme.breakpoints.down('sm')]: {
            minWidth: '5rem',
            padding: '0.15rem 0.3rem 0.15rem 0.3rem',
            fontSize: '0.75rem',
          },
        }),
        scoreCell: ({ theme }: { theme: Theme }) => ({
          textAlign: 'right',
          whiteSpace: 'nowrap',
          fontSize: '0.8rem',
          padding: '0.2rem',
          [theme.breakpoints.down('md')]: { fontSize: '0.75rem', padding: '0.15rem' },
          [theme.breakpoints.down('sm')]: { fontSize: '0.75rem' },
        }),
      },
    },
    GratiScoreTable: {
      styleOverrides: {
        root: {
          borderCollapse: 'collapse',
          width: '100%',
        },
        headerCell: ({ theme }: { theme: Theme }) => ({
          textAlign: 'left',
          fontWeight: 'bold',
          whiteSpace: 'nowrap',
          fontSize: '0.8rem',
          padding: '0.2rem',
          borderBottom: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.action.hover,
          [theme.breakpoints.down('md')]: { fontSize: '0.7rem', padding: '0.15rem' },
          [theme.breakpoints.down('sm')]: { fontSize: '0.65rem' },
        }),
        subHeaderCell: ({ theme }: { theme: Theme }) => ({
          borderLeft: `2px solid ${theme.palette.divider}`,
          borderRight: `2px solid ${theme.palette.divider}`,
        }),
        nameHeaderCell: ({ theme }: { theme: Theme }) => ({
          textAlign: 'left',
          fontWeight: 'bold',
          whiteSpace: 'nowrap',
          fontSize: '0.8rem',
          minWidth: '7rem',
          padding: '0.2rem 0.5rem 0.2rem 0.4rem',
          borderBottom: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.action.hover,
          [theme.breakpoints.down('md')]: {
            minWidth: '6rem',
            padding: '0.15rem 0.4rem 0.15rem 0.35rem',
            fontSize: '0.7rem',
          },
          [theme.breakpoints.down('sm')]: {
            minWidth: '5rem',
            padding: '0.15rem 0.3rem 0.15rem 0.3rem',
            fontSize: '0.65rem',
          },
        }),
        parCell: ({ theme }: { theme: Theme }) => ({
          textAlign: 'left',
          whiteSpace: 'nowrap',
          fontSize: '0.8rem',
          padding: '0.2rem',
          backgroundColor: theme.palette.action.hover,
          color: theme.palette.text.secondary,
          [theme.breakpoints.down('md')]: { fontSize: '0.7rem', padding: '0.15rem' },
          [theme.breakpoints.down('sm')]: { fontSize: '0.65rem' },
        }),
        subParCell: ({ theme }: { theme: Theme }) => ({
          borderLeft: `2px solid ${theme.palette.divider}`,
          borderRight: `2px solid ${theme.palette.divider}`,
        }),
        parNameCell: ({ theme }: { theme: Theme }) => ({
          textAlign: 'left',
          whiteSpace: 'nowrap',
          fontSize: '0.8rem',
          minWidth: '7rem',
          padding: '0.2rem 0.5rem 0.2rem 0.4rem',
          backgroundColor: theme.palette.action.hover,
          color: theme.palette.text.secondary,
          [theme.breakpoints.down('md')]: {
            minWidth: '6rem',
            padding: '0.15rem 0.4rem 0.15rem 0.35rem',
            fontSize: '0.7rem',
          },
          [theme.breakpoints.down('sm')]: {
            minWidth: '5rem',
            padding: '0.15rem 0.3rem 0.15rem 0.3rem',
            fontSize: '0.65rem',
          },
        }),
        dataCell: ({ theme }: { theme: Theme }) => ({
          textAlign: 'left',
          fontSize: '0.8rem',
          padding: '0.2rem 0.3rem',
          [theme.breakpoints.down('md')]: { fontSize: '0.7rem', padding: '0.15rem 0.2rem' },
          [theme.breakpoints.down('sm')]: { fontSize: '0.65rem', padding: '0.15rem' },
        }),
        subCell: ({ theme }: { theme: Theme }) => ({
          textAlign: 'left',
          fontWeight: 'bold',
          fontSize: '0.8rem',
          padding: '0.2rem 0.3rem',
          backgroundColor: theme.palette.action.hover,
          borderLeft: `2px solid ${theme.palette.divider}`,
          borderRight: `2px solid ${theme.palette.divider}`,
          [theme.breakpoints.down('md')]: { fontSize: '0.7rem', padding: '0.15rem 0.2rem' },
          [theme.breakpoints.down('sm')]: { fontSize: '0.65rem', padding: '0.15rem' },
        }),
        nameCell: ({ theme }: { theme: Theme }) => ({
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          fontSize: '0.8rem',
          minWidth: '7rem',
          padding: '0.2rem 0.5rem 0.2rem 0.4rem',
          [theme.breakpoints.down('md')]: {
            minWidth: '6rem',
            padding: '0.15rem 0.4rem 0.15rem 0.35rem',
            fontSize: '0.7rem',
          },
          [theme.breakpoints.down('sm')]: {
            minWidth: '5rem',
            padding: '0.15rem 0.3rem 0.15rem 0.3rem',
            fontSize: '0.65rem',
          },
        }),
        teamColorCell: {
          padding: 0,
          width: '0.3em',
          minWidth: '0.3em',
          maxWidth: '0.3em',
        },
        groupSpacerCell: ({ theme }: { theme: Theme }) => ({
          height: '0.5rem',
          padding: 0,
          border: 'none',
          borderTop: `2px solid ${theme.palette.divider}`,
        }),
      },
    },
    GratiScoreEntry: {
      styleOverrides: {
        container: {
          overflowX: 'auto',
          width: '100%',
        },
        table: ({ theme }: { theme: Theme }) => ({
          borderCollapse: 'separate',
          borderSpacing: 0,
          minWidth: 'max-content',
          '& tr:focus-within > td': {
            backgroundColor: alpha(theme.palette.primary.main, 0.10),
          },
        }),
        headerCell: ({ theme }: { theme: Theme }) => ({
          textAlign: 'left',
          fontWeight: 'bold',
          whiteSpace: 'nowrap',
          fontSize: '0.75rem',
          padding: '0.2rem',
          borderBottom: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.action.hover,
          [theme.breakpoints.up('sm')]: { fontSize: '0.8rem' },
        }),
        subHeaderCell: ({ theme }: { theme: Theme }) => ({
          borderLeft: `2px solid ${theme.palette.divider}`,
          borderRight: `2px solid ${theme.palette.divider}`,
        }),
        nameHeaderCell: ({ theme }: { theme: Theme }) => ({
          position: 'sticky',
          left: 0,
          zIndex: 3,
          textAlign: 'left',
          fontWeight: 'bold',
          whiteSpace: 'nowrap',
          fontSize: '0.75rem',
          minWidth: '6rem',
          padding: '0.2rem 0.4rem 0.2rem 0',
          borderBottom: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.action.hover,
          [theme.breakpoints.up('sm')]: { fontSize: '0.8rem', minWidth: '7rem', padding: '0.2rem 0.5rem 0.2rem 0.4rem' },
        }),
        actionsHeaderCell: ({ theme }: { theme: Theme }) => ({
          borderBottom: `1px solid ${theme.palette.divider}`,
          width: '2rem',
        }),
        parCell: ({ theme }: { theme: Theme }) => ({
          textAlign: 'left',
          whiteSpace: 'nowrap',
          fontSize: '0.75rem',
          padding: '0.2rem',
          backgroundColor: theme.palette.action.hover,
          color: theme.palette.text.secondary,
          [theme.breakpoints.up('sm')]: { fontSize: '0.8rem' },
        }),
        subParCell: ({ theme }: { theme: Theme }) => ({
          borderLeft: `2px solid ${theme.palette.divider}`,
          borderRight: `2px solid ${theme.palette.divider}`,
        }),
        parNameCell: ({ theme }: { theme: Theme }) => ({
          position: 'sticky',
          left: 0,
          zIndex: 2,
          textAlign: 'left',
          whiteSpace: 'nowrap',
          fontSize: '0.75rem',
          minWidth: '6rem',
          padding: '0.2rem 0.4rem 0.2rem 0',
          backgroundColor: theme.palette.action.hover,
          color: theme.palette.text.secondary,
          [theme.breakpoints.up('sm')]: { fontSize: '0.8rem', minWidth: '7rem', padding: '0.2rem 0.5rem 0.2rem 0.4rem' },
        }),
        nameCell: ({ theme }: { theme: Theme }) => ({
          position: 'sticky',
          left: 0,
          zIndex: 2,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          fontSize: '0.75rem',
          minWidth: '6rem',
          padding: '0.2rem 0.4rem 0.2rem 0',
          backgroundColor: theme.palette.background.paper,
          borderBottom: `1px solid ${theme.palette.divider}`,
          [theme.breakpoints.up('sm')]: { fontSize: '0.8rem', minWidth: '7rem', padding: '0.2rem 0.5rem 0.2rem 0.4rem' },
        }),
        dataCell: ({ theme }: { theme: Theme }) => ({
          textAlign: 'left',
          fontSize: '0.75rem',
          padding: '0.1rem',
          borderBottom: `1px solid ${theme.palette.divider}`,
          [theme.breakpoints.up('sm')]: { fontSize: '0.8rem', padding: '0.15rem' },
        }),
        subCell: ({ theme }: { theme: Theme }) => ({
          textAlign: 'left',
          fontWeight: 'bold',
          fontSize: '0.75rem',
          padding: '0.1rem 0.2rem',
          backgroundColor: theme.palette.action.hover,
          borderLeft: `2px solid ${theme.palette.divider}`,
          borderRight: `2px solid ${theme.palette.divider}`,
          borderBottom: `1px solid ${theme.palette.divider}`,
          [theme.breakpoints.up('sm')]: { fontSize: '0.8rem', padding: '0.15rem 0.3rem' },
        }),
        actionsCell: ({ theme }: { theme: Theme }) => ({
          borderBottom: `1px solid ${theme.palette.divider}`,
          width: '2rem',
          padding: '0 0.1rem',
        }),
        resultCell: ({ theme }: { theme: Theme }) => ({
          fontStyle: 'italic',
          fontSize: '0.75rem',
          padding: '0.1rem 0.4rem',
          borderBottom: `1px solid ${theme.palette.divider}`,
          [theme.breakpoints.up('sm')]: { fontSize: '0.8rem' },
        }),
        input: ({ theme }: { theme: Theme }) => ({
          textAlign: 'left',
          padding: '0.2rem',
          width: '2em',
          fontSize: '0.75rem',
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: '4px',
          outline: 'none',
          color: theme.palette.text.primary,
          '&:focus': {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: '1px',
            borderColor: 'transparent',
          },
          [theme.breakpoints.up('sm')]: { width: '2.2em', fontSize: '0.8rem' },
        }),
        groupSpacerCell: ({ theme }: { theme: Theme }) => ({
          height: '0.5rem',
          padding: 0,
          border: 'none',
          borderTop: `2px solid ${theme.palette.divider}`,
        }),
      },
    },
    GratiColorPicker: {
      styleOverrides: {
        root: ({ theme }) =>
          theme.unstable_sx({
            position: 'relative',
            display: 'inline-block',
          }),
        colorButton: ({ theme }) =>
          theme.unstable_sx({
            width: '2rem',
            height: '2rem',
            borderRadius: '50%',
            border: '2px solid',
            borderColor: 'divider',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '&:hover': {
              opacity: 0.8,
            },
          }),
        dropdown: ({ theme }) =>
          theme.unstable_sx({
            position: 'absolute',
            top: '100%',
            left: 0,
            zIndex: 1300,
            mt: 1,
            p: 1,
            minWidth: '200px',
            backgroundColor: 'surface.container',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            boxShadow: 8,
          }),
        colorGrid: ({ theme }) =>
          theme.unstable_sx({
            display: 'grid',
            gridTemplateColumns: 'repeat(8, 1fr)',
            gap: 0.5,
            p: 1,
          }),
        colorOption: ({ theme }) =>
          theme.unstable_sx({
            width: '1.5rem',
            height: '1.5rem',
            borderRadius: '50%',
            cursor: 'pointer',
            border: '1px solid',
            borderColor: 'transparent',
            '&:hover': {
              transform: 'scale(1.1)',
              boxShadow: 2,
            },
          }),
        selectedColorOption: ({ theme }) =>
          theme.unstable_sx({
            borderColor: 'primary.main',
          }),
        colorLabel: ({ theme }) =>
          theme.unstable_sx({
            typography: 'bodySmall',
            color: 'text.secondary',
            px: 1,
            py: 0.5,
          }),
      },
    },
    GratiDirectory: {
      styleOverrides: {
        root: ({ theme, ownerState }: { theme: Theme; ownerState?: { sticky?: boolean; columns?: 1 | 2 | 3; stickyTopOffset?: 'appBar' | 'tabBar' } }) => {
          const columns = ownerState?.columns ?? 1;
          const hasTabBarAbove = ownerState?.stickyTopOffset === 'tabBar';
          return theme.unstable_sx({
            width: '100%',
            height: '100%',
            position: 'relative',
            overflow: ownerState?.sticky ? 'visible' : 'auto',
            p: 0,
            '& ul': { padding: 0 },
            '& > .MuiListSubheader-root': ownerState?.sticky ? {
              position: 'sticky',
              top: {
                xs: hasTabBarAbove ? `calc(${APPBAR_STICKY_TOP.xs} + ${theme.spacing(TAB_BAR_HEIGHT)})` : APPBAR_STICKY_TOP.xs,
                sm: hasTabBarAbove ? `calc(${APPBAR_STICKY_TOP.sm} + ${theme.spacing(TAB_BAR_HEIGHT)})` : APPBAR_STICKY_TOP.sm,
              },
              zIndex: theme.zIndex.appBar - 2,
              backgroundColor: 'surface.main',
            } : {},
            // Multi-column grid layout for md+ screens
            ...(columns > 1 ? {
              [theme.breakpoints.up('md')]: {
                display: 'grid',
                gridTemplateColumns: `repeat(${columns}, 1fr)`,
                '& > .MuiListSubheader-root': {
                  gridColumn: '1 / -1',
                },
                '& > *:not(.MuiListSubheader-root)': {
                  minWidth: 0,
                },
              },
            } : {}),
          });
        },
      }
    },
    GratiDirectorySubheader: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: 'var(--mui-palette-surface-contrastText)',
          backgroundColor: 'var(--mui-palette-surface-main)',
          flex: 1,
          padding: theme.spacing(1, 0),
          '&:hover': {
            backgroundColor: 'var(--mui-palette-surface-containerHigh)',
          },
        }),
        text: ({ theme }) => ({
          ...theme.typography.titleMedium,
          backgroundColor: 'transparent',
        }),
      }
    },
    GratiDirectoryItem: {
      styleOverrides: {
        root: ({ theme }) =>
          theme.unstable_sx({
            py: 1,
            px: 0,
            '& .MuiAvatar-circular': {
              backgroundColor: 'primary.container',
              color: 'primary.onContainer',
            },
            '& .GratiDirectoryItemBadge-container': {
              position: 'absolute',
              bottom: -4,
              right: -4,
            },
            '& .GratiDirectoryItemBadge-icon': {
              fontSize: '1.2rem',
              color: 'primary.main',
              backgroundColor: 'background.paper',
              borderRadius: '50%',
            },
          }),
        content: ({ theme }) =>
          theme.unstable_sx({
            py: 1
          }),
        icon: ({ theme }) =>
          theme.unstable_sx({
            py: 1
          })
      }
    },
    GratiTabBar: {
      styleOverrides: {
        root: ({ theme }) => ({
          ...theme.unstable_sx({
            top: {
              xs: `calc(${APPBAR_STICKY_TOP.xs} + var(--grati-sticky-card-height, 0px))`,
              sm: `calc(${APPBAR_STICKY_TOP.sm} + var(--grati-sticky-card-height, 0px))`,
            },
          }),
          position: 'sticky',
          zIndex: theme.zIndex.appBar - 1,
          backgroundColor: (theme.palette as unknown as Record<string, { main: string }>)['surface']?.main ?? theme.palette.background.paper,
        }),
      }
    },
    GratiActionBarContainer: {
      styleOverrides: {
        root: ({ theme, ownerState }: { theme: Theme; ownerState?: { position?: 'top' | 'bottom' } }) => {
          const isTop = ownerState?.position === 'top';
          return theme.unstable_sx({
            position: 'sticky',
            ...(isTop ? { top: theme.spacing(APP_BAR_HEIGHT) } : { bottom: 0 }),
            display: 'flex',
            flexDirection: 'column',
            mx: -1,
            ...(isTop ? { mt: -1 } : { pb: 'env(safe-area-inset-bottom, 0px)' }),
            zIndex: theme.zIndex.appBar - 1,
          });
        },
      }
    },
    GratiActionBar: {
      styleOverrides: {
        root: ({ theme, ownerState }: { theme: Theme; ownerState?: { variant?: string } }) => {
          const baseStyles = theme.unstable_sx({
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 1,
            p: 1,
          });

          const variantStyles = (() => {
            switch (ownerState?.variant) {
              case 'god':
                return theme.unstable_sx({
                  backgroundColor: 'tertiary.container',
                  color: 'tertiary.onContainer',
                });
              case 'page':
                return theme.unstable_sx({
                  backgroundColor: 'surface.container',
                  color: 'surface.onContainer',
                });
              default:
                return theme.unstable_sx({
                  backgroundColor: 'secondary.container',
                  color: 'secondary.onContainer',
                });
            }
          })();

          return { ...baseStyles, ...variantStyles };
        },
      }
    },

    GratiSection: {
      styleOverrides: {
        root: ({ theme, ownerState }: { theme: Theme; ownerState?: { headerStyle?: string; isEditHighlighted?: boolean } }) => {
          const base = theme.unstable_sx({
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
          });
          const editingStyles = ownerState?.isEditHighlighted ? theme.unstable_sx({
            borderRadius: 1,
            border: '2px solid',
            borderColor: 'primary.main',
            backgroundColor: 'primary.container',
            p: 0.5,
          }) : {};
          return { ...base, ...editingStyles };
        },
        header: ({ theme, ownerState }: { theme: Theme; ownerState?: { headerStyle?: string; publishState?: string } }) => {
          const base = theme.unstable_sx({
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 1,
            minHeight: '2.5rem',
          });
          // A draft's header is tinted to back up the "Draft" its publish switch says — never the
          // only signal
          const draftTint = ownerState?.publishState === 'draft'
            ? { backgroundColor: alpha(theme.palette.warning.main, 0.14) }
            : {};
          switch (ownerState?.headerStyle) {
            case 'filled':
              return { ...base, ...theme.unstable_sx({ backgroundColor: 'surface.containerHigh', borderRadius: '4px 4px 0 0', px: 1, py: 0.5 }), ...draftTint };
            case 'outlined':
              return { ...base, ...theme.unstable_sx({ border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 1, py: 0.5 }), ...draftTint };
            default:
              return { ...base, ...draftTint };
          }
        },
        // The open section's actions, at the top of its content: main action left, controls and ⋮ right
        toolbar: ({ theme }: { theme: Theme }) => theme.unstable_sx({
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1,
          minHeight: '2.5rem',
        }),
        // "Admins only" above the admin actions in a section's ⋮ menu — the app's eye-off marker
        menuAdminLabel: ({ theme }: { theme: Theme }) => theme.unstable_sx({
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          px: 2,
          pt: 1,
          pb: 0.5,
          typography: 'labelSmall',
          color: 'text.secondary',
          listStyle: 'none',
        }),
        // The inline "Hide this from members? Cancel / Unpublish" row under a published header
        unpublishConfirm: ({ theme }: { theme: Theme }) => theme.unstable_sx({
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1,
          px: 1,
          py: 0.5,
          backgroundColor: alpha(theme.palette.warning.main, 0.08),
          // The question takes the room; the actions stay together at the end (Material banner),
          // wrapping as a pair below the question when the row is too narrow
          '& > :first-child': { flexGrow: 1 },
          '& > :last-child': { display: 'flex', gap: 1, ml: 'auto' },
        }),
      },
    },
    // A key explains the symbols used in nearby content, so it sits on its own tonal surface (MD3
    // surface-container-low) rather than an outline — content and section headers use outlines and
    // containerHigh, so the shading alone sets the key apart from what it explains.
    EventResultEntrySummary: {
      styleOverrides: {
        // The viewer's own row: tinted wherever it shows, set a little apart from the top places when
        // the rows between them are skipped
        viewer: ({ theme, ownerState }: { theme: Theme; ownerState?: { isDetached?: boolean } }) => theme.unstable_sx({
          backgroundColor: alpha(theme.palette.primary.main, 0.1),
          borderRadius: 1,
          ...(ownerState?.isDetached && { mt: 1.5 }),
        }),
      },
    },
    GratiEditFab: {
      styleOverrides: {
        // Editing: the FAB takes the strong primary colour, so the mode is unmistakable. The colour
        // change and the icon swap use Material 3's emphasized easing at medium duration.
        root: ({ theme, ownerState }: { theme: Theme; ownerState?: { isEditing?: boolean } }) => theme.unstable_sx({
          gap: 1,
          transition: theme.transitions.create(['background-color', 'color', 'box-shadow'], {
            duration: theme.transitions.duration.standard,
            easing: 'cubic-bezier(0.2, 0, 0, 1)',
          }),
          ...(ownerState?.isEditing && {
            backgroundColor: 'primary.main',
            color: 'primary.contrastText',
            '&:hover': { backgroundColor: 'primary.dark', color: 'primary.contrastText' },
          }),
          '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
        }),
        // The pencil turns and fades into the check (and back)
        icons: ({ theme, ownerState }: { theme: Theme; ownerState?: { isEditing?: boolean } }) => {
          const motion = theme.transitions.create(['opacity', 'transform'], {
            duration: theme.transitions.duration.standard,
            easing: 'cubic-bezier(0.2, 0, 0, 1)',
          });
          const shown = { opacity: 1, transform: 'rotate(0deg) scale(1)' };
          return {
            position: 'relative',
            display: 'inline-flex',
            width: '1.5rem',
            height: '1.5rem',
            '& > svg': { position: 'absolute', inset: 0, transition: motion },
            '& > .GratiEditFab-edit': ownerState?.isEditing ? { opacity: 0, transform: 'rotate(90deg) scale(0.6)' } : shown,
            '& > .GratiEditFab-done': ownerState?.isEditing ? shown : { opacity: 0, transform: 'rotate(-90deg) scale(0.6)' },
            '@media (prefers-reduced-motion: reduce)': { '& > svg': { transition: 'none' } },
          };
        },
      },
    },
    GratiIconSwitch: {
      styleOverrides: {
        // The switch colour per variant (GratiIconSwitchVariant): the thumb disc fills with it.
        root: ({ theme, ownerState }: { theme: Theme; ownerState?: { variant?: string } }) => {
          const offColor = ownerState?.variant === 'yesNo' ? 'error.main'
            : ownerState?.variant === 'visibility' ? 'warning.main'
            : 'secondary.main';
          return theme.unstable_sx({
            '& .MuiSwitch-switchBase': {
              color: offColor,
              '&.Mui-checked': { color: 'primary.main' },
              // Disabled keeps its state colour, muted, so it still reads as Draft / Published
              // (or yes / no) and not as a third state; the track stays visible in either mode
              '&.Mui-disabled': { color: offColor, opacity: 0.55 },
              '&.Mui-checked.Mui-disabled': { color: 'primary.main' },
            },
            // && — must outrank the disabled-track rules (MUI's and MuiSwitch's below), which fade the
            // track to the page colour; disabled keeps the enabled track, only the knob is muted
            '&& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track': {
              opacity: theme.palette.mode === 'dark' ? 0.3 : 0.38,
              backgroundColor: theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.common.black,
            },
            '&& .MuiSwitch-switchBase.Mui-checked.Mui-disabled + .MuiSwitch-track': {
              backgroundColor: theme.palette.primary.main,
            },
          });
        },
        // The thumb disc: the thumb's size, filled with the switch colour, content in the surface colour
        thumb: ({ theme }: { theme: Theme }) => theme.unstable_sx({
          width: theme.spacing(2.5),
          height: theme.spacing(2.5),
          borderRadius: '50%',
          backgroundColor: 'currentColor',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          '& > *': { color: 'surface.main' },
          '& .MuiSvgIcon-root': { fontSize: '0.85em' },
        }),
        thumbText: {
          fontSize: '0.6rem',
          fontWeight: 700,
          lineHeight: 1,
        },
      },
    },
    GratiPublishSwitch: {
      styleOverrides: {
        // The state word, then the switch, as one tap target
        root: ({ theme }: { theme: Theme }) => theme.unstable_sx({
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          cursor: 'pointer',
          whiteSpace: 'nowrap',
        }),
      },
    },
    GratiKey: {
      styleOverrides: {
        root: ({ theme, ownerState }: { theme: Theme; ownerState?: { isExpanded?: boolean; hasDetails?: boolean } }) => theme.unstable_sx({
          // Full width by default, so the panel lines up with the section header above it.
          display: 'block',
          boxSizing: 'border-box',
          width: '100%',
          position: 'relative',
          // The whole panel is a tap target that opens or closes it
          ...(ownerState?.hasDetails && { cursor: 'pointer' }),
          backgroundColor: 'surface.containerLow',
          color: 'surface.onContainer',
          borderRadius: '0.75rem',
          px: 1.5,
          py: 1,
          // Room for the expand icon in the upper right (after px, which it overrides)
          ...(ownerState?.hasDetails && { pr: 4.5 }),
          // Every group rendered null — no panel
          '&:has(> .GratiKey-clip > .GratiKey-content:empty)': { display: 'none' },
          // Directly under a section header (filled or outlined), leave a gap so the panel doesn't
          // butt against the header — matching the gap a key gets below a page-level title. Also one
          // wrapper deep, for content that stacks the key with what it explains (e.g. "No tee time").
          '.GratiSection-contentBelowHeader > &:first-child, .GratiSection-contentBelowHeader > :first-child > &:first-child': { mt: 1 },
        }),
        // Slides between the collapsed and expanded heights (set inline from the content's measured
        // height) — the two are different layouts, so only the height can animate between them.
        clip: ({ theme }: { theme: Theme }) => ({
          overflow: 'hidden',
          transition: theme.transitions.create('height', { duration: theme.transitions.duration.standard }),
          '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
        }),
        content: ({ theme, ownerState }: { theme: Theme; ownerState?: { isExpanded?: boolean } }) => theme.unstable_sx({
          display: 'flex',
          flexWrap: 'wrap',
          // Expanded, groups sit side by side as blocks. Collapsed, the groups and their entry rows
          // dissolve (display: contents) so every group's entries flow together in one wrapping row.
          ...(ownerState?.isExpanded
            ? { columnGap: 3, rowGap: 1 }
            : {
              alignItems: 'center',
              columnGap: 1.5,
              rowGap: 0.5,
              '& .GratiKey-groupCollapsed, & .GratiKey-itemsCollapsed': { display: 'contents' },
            }),
        }),
        group: ({ theme }: { theme: Theme }) => theme.unstable_sx({
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 0.5,
        }),
        title: ({ theme }: { theme: Theme }) => theme.unstable_sx({
          display: 'flex',
          alignItems: 'center',
          color: 'text.secondary',
        }),
        items: ({ theme, ownerState }: { theme: Theme; ownerState?: { direction?: string } }) => theme.unstable_sx(
          ownerState?.direction === 'column'
            // Each item flattens into the grid (symbol | label | detail), so labels and details line
            // up across rows; explicit columns keep an item without a detail from shifting the next.
            ? {
              display: 'grid',
              // minmax(0, …) lets long details wrap instead of widening the panel past the screen
              gridTemplateColumns: 'auto auto minmax(0, 1fr)',
              alignItems: 'center',
              columnGap: 0.75,
              rowGap: 0.5,
              // && — must outrank the item slot's own display: flex
              '&& > *': { display: 'contents' },
              '& > * > :nth-child(1)': { gridColumn: 1 },
              '& > * > :nth-child(2)': { gridColumn: 2 },
              '& > * > :nth-child(3)': { gridColumn: 3 },
            }
            : {
              display: 'flex',
              flexWrap: 'wrap',
              columnGap: 1.5,
              rowGap: 0.5,
              '&:empty': { display: 'none' },
            }
        ),
        item: ({ theme }: { theme: Theme }) => theme.unstable_sx({
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
        }),
        detail: ({ theme }: { theme: Theme }) => theme.unstable_sx({
          color: 'text.secondary',
          ml: 0.25,
        }),
        note: ({ theme }: { theme: Theme }) => theme.unstable_sx({
          color: 'text.secondary',
        }),
        // A plain color sample, for keys whose symbol is a color rather than a shape (shapes carry
        // their own meaning — see GratiMarker).
        swatch: ({ theme }: { theme: Theme }) => theme.unstable_sx({
          width: '0.75em',
          height: '0.75em',
          borderRadius: '50%',
          flexShrink: 0,
        }),
        // The expand / collapse icon, in the upper right
        toggle: ({ theme }: { theme: Theme }) => theme.unstable_sx({
          position: 'absolute',
          top: '0.25rem',
          right: '0.25rem',
          color: 'text.secondary',
        }),
      },
    },
    GratiMarker: {
      defaultProps: {
        shape: 'circle',
        size: 'md',
      },
    },
    GratiMarkerEditor: {
      defaultProps: {
        shape: 'circle',
        size: 'lg',
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: ({ theme }) =>
          theme.unstable_sx({
            backgroundColor: 'surface.container',
            color: 'surface.contrastText',
            display: 'block',
            height: theme.spacing(APP_BAR_HEIGHT-1),
            minHeight: theme.spacing(APP_BAR_HEIGHT-1),
            my: 0,
            pb: 1,
            typography: {
              fontSize: '2rem',
            },
            '.MuiToolbar-root': {
              backgroundColor: 'surface.container',
              borderBottom: '1px solid',
              borderBottomColor: 'outline.variant',
              color: 'surface.contrastText',
              pr: '.5rem',
            },
            '.NavMenu': {
              pl: '1rem',
            },
            '.AppBarIcon': {
              color: 'surface.onVariant',
              '&:hover': {
                backgroundColor: 'surface.containerHigh',
                borderRadius: '50%',
              },
            },
            '.AppBarText': {
              color: 'surface.onVariant',
              fontFamily: 'sans-serif',
              fontSize: { xs: '.8rem', sm: '1rem', md: '1.25rem' },
              fontWeight: 'inherit',
              letterSpacing: '0rem',
              textDecoration: 'none',
            },
            '.AppBarText-home': {
              color: 'surface.onVariant',
              fontFamily: 'monospace',
              fontSize: '1rem',
              fontWeight: 700,
              letterSpacing: '.3rem',
            },
            '&.emulator-mode .MuiToolbar-root': {
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                inset: 0,
                backgroundImage: EMULATOR_WATERMARK_BG,
                backgroundRepeat: 'repeat',
                pointerEvents: 'none',
              },
            },
          }),
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        listbox: ({ theme }) =>
          theme.unstable_sx({
            backgroundColor: 'primary.container',
            color: 'primary.onContainer',
            '& .MuiListItem-root': {
              backgroundColor: 'primary.container',
              color: 'primary.onContainer',
              '&:hover': {
                backgroundColor: 'primary.containerHigh',
              },
              '&.Mui-focused': {
                backgroundColor: 'primary.containerHigh',
              },
            },
            '& .unclaimed': {
              opacity: 0.75,
              fontStyle: 'italic',
            },
          }),
        paper: ({ theme }) =>
          theme.unstable_sx({
            backgroundColor: 'primary.container',
            color: 'primary.onContainer',
          }),
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: ({ theme }) =>
          theme.unstable_sx({
            color: 'surface.contrastText',
            backgroundColor: 'surface.main',
            '& .MuiAvatar-img': {
              backgroundColor: 'primary.inverse',
            },
          }),

      },
    },
    MuiButton: {
      defaultProps: {
        size: 'small',
      },
      variants: [
        {
          props: { size: 'small' },
          style: {
            height: '2.5rem',
            lineHeight: '1rem',
            minWidth: '5rem',
          },
        },
        {
          props: { size: 'medium' },
          style: {
            height: '3.5rem',
            minWidth: '7rem',
            px: '1.5rem',
          },
        },
        {
          props: { size: 'large' },
          style: {
            height: '6rem',
            minWidth: '10rem',
            px: '3rem',
          },
        },
      ],
      styleOverrides: {
        root: ({ theme }) =>
          theme.unstable_sx({
            borderRadius: (theme.shape.borderRadius as number) * 2,
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '1rem',
            height: '2.5rem',
            letterSpacing: '0.023rem',
            lineHeight: '1rem',
            minWidth: '5rem',
            px: '1rem',
            '&:not(.MuiButton-contained):hover': {
              backgroundColor: 'surface.containerHigh',
            },
          }),
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: ({ theme }) =>
          theme.unstable_sx({
            color: 'surface.contrastText',
            backgroundColor: 'surface.main',
            '&.BodyContainer': {
              color: 'surface.contrastText',
              typography: 'bodyMedium',
              px: '0rem',
              pt: '0rem',
            },
          }),
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: ({ theme }) =>
          theme.unstable_sx({
            mt: '.25rem',
            mb: '.5rem',
            borderColor: 'divider',
          }),
        middle: ({ theme }) =>
          theme.unstable_sx({
            mx: '1rem',
          }),
      },
    },
    MuiDrawer: {
      styleOverrides: {
        root: ({ theme }) =>
          theme.unstable_sx({
            backgroundColor: 'surface.container',
            color: 'surface.contrastText',
            borderRight: '1px solid',
            borderRightColor: 'outline.variant',
            width: '5rem',
            fontSize: '1rem',
            flexShrink: 0,
            overflow: 'auto',
            '& .MuiDrawer-paper': {
              width: '5rem',
              backgroundColor: 'surface.container',
              color: 'surface.contrastText',
              boxSizing: 'border-box',
            },
            '.MuiListItem-root': {
              backgroundColor: 'surface.container',
              color: 'surface.onContainer',
            },
            '.MuiIconButton-root': {
              typography: 'iconbutton',
              flexDirection: 'column',
              width: '100%',
              backgroundColor: 'surface.container',
              color: 'surface.onContainer',
            },
            '& .MuiIconButton-root:hover': {
              width: '100%',
            },
          }),
      },
    },
    MuiFab: {
      styleOverrides: {
        root: ({ theme }) =>
          theme.unstable_sx({
            position: 'fixed',
            bottom: '2rem',
            right: '2rem',
            boxShadow: 3,
            zIndex: theme.zIndex.appBar,
            backgroundColor: 'secondary.container',
            color: 'secondary.onContainer',
            '&:hover': {
              boxShadow: 5,
              backgroundColor: 'secondary.container',
              color: 'secondary.onContainer',
            },
          }),
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: ({ theme }) =>
          theme.unstable_sx({
            gap: theme.spacing(2),
            py: 1,
            backgroundColor: 'transparent',
            '&.Mui-selected': {
              backgroundColor: 'action.selected',
            },
            '&.test': {
              border: '2px dashed',
              borderColor: 'tertiary.main',
            },
            '&.admin': {
              backgroundColor: 'primary.container',
              color: 'primary.onContainer',
            },
            // Semantic status classes — use explicit color tokens rather than opacity
            // so contrast is maintained against all backgrounds (WCAG compliance)
            '&.inactive': {
              '& .MuiTypography-root': { color: 'var(--mui-palette-text-disabled)' },
              '& .MuiSvgIcon-root': { color: 'var(--mui-palette-text-disabled)' },
              opacity: 0.6,
            },
            '&.draft': {
              '& .MuiTypography-root': { color: 'var(--mui-palette-text-disabled)' },
              fontStyle: 'italic',
            },
            '&.active': {
              // Active items styled normally (default)
            },
            '&.archived': {
              '& .MuiTypography-root': { color: 'var(--mui-palette-text-disabled)' },
              '& .MuiSvgIcon-root': { color: 'var(--mui-palette-text-disabled)' },
              opacity: 0.6,
            },
            '&.deleted': {
              '& .MuiTypography-root': { color: 'var(--mui-palette-text-disabled)' },
              '& .MuiSvgIcon-root': { color: 'var(--mui-palette-text-disabled)' },
              opacity: 0.4,
            },
            '&.cancelled': {
              '& .MuiTypography-root': { color: 'var(--mui-palette-error-main)' },
              '& .MuiSvgIcon-root': { color: 'var(--mui-palette-error-main)' },
              opacity: 0.7,
            },
            '&.unclaimed': {
              opacity: 0.75,
              '& .MuiTypography-root': { fontStyle: 'italic' },
            },
          }),
      },
    },
    MuiListItemAvatar: {
      styleOverrides: {
        root: ({ theme }) =>
          theme.unstable_sx({
            color: 'surface.onVariant',
          }),
      },
    },
    MuiListItemText: {
      styleOverrides: {
        root: ({ theme }) =>
          theme.unstable_sx({
            color: 'surface.onVariant',
          }),
        primary: ({ theme }) =>
          theme.unstable_sx({
            '&.title': {
              color: 'surface.contrastText',
              variant: 'title',
            },
          }),
        secondary: ({ theme }) =>
          theme.unstable_sx({
            color: 'surface.onVariant',
          }),
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: ({ theme }) =>
          theme.unstable_sx({
            color: 'primary.onFixedVariant',
            '&.title': {
              color: 'surface.contrastText',
              variant: 'title',
            },
          }),
      },
    },
    MuiListSubheader: {
      styleOverrides: {
        root: ({ theme }) =>
          theme.unstable_sx({
            color: 'surface.contrastText',
          }),
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: ({ theme }) =>
          theme.unstable_sx({
            backgroundColor: 'surface.container',
            py: 1, // 8dp vertical padding
          }),
        list: ({ theme }) =>
          theme.unstable_sx({
            py: 0, // Remove default list padding since we added it to paper
          }),
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: ({ theme }) =>
          theme.unstable_sx({
            minHeight: '48px', // MD3 standard height
            px: 1.5, // 12dp horizontal padding
            py: 1.5, // 12dp vertical padding
            '&:hover': {
              backgroundColor: 'action.hover',
            },
            '&.Mui-selected': {
              backgroundColor: 'secondary.container',
              color: 'secondary.onContainer',
              '&:hover': {
                backgroundColor: 'secondary.container',
              },
            },
          }),
      },
    },
    MuiStack: {
      styleOverrides: {
        root: ({ theme }) =>
          theme.unstable_sx({
            mx: 0,
            px: 0,
            py: 0,
            '&.AppStack': {
              backgroundColor: 'surface.main',
              color: 'surface.contrastText',
              display: 'flex',
              FlexGrow: 1,
            },
            '&.TableStack': {
              my: '0rem',
              pb: '.25rem',
            },
            '&.TableStack &.TableStack': {
              my: '0rem',
              pb: '.25rem',
            },
            '&.test': {
              border: '2px dashed',
              borderColor: 'tertiary.main',
            },
          }),
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: ({ theme }) =>
          theme.unstable_sx({
            '& .MuiSwitch-switchBase': {
              '&.Mui-checked': {
                color: 'primary.fixed',
                '& + .MuiSwitch-track': {
                  backgroundColor: 'primary.main',
                },
              },
              '&.Mui-disabled': {
                color: 'outline.main',
                '& + .MuiSwitch-track': {
                  backgroundColor: 'surface.container',
                },
              },
            },
            '& .MuiSwitch-track': {
              borderRadius: theme.spacing(1.37),
              '&::before, &::after': {
                content: '""',
                position: 'absolute',
                top: '50%',
                transform: 'translateY(-50%)',
                width: theme.spacing(2.5),
                height: theme.spacing(2.5),
              },
            },
            '& .MuiSwitch-thumb': {
              boxShadow: 'none',
              width: theme.spacing(2),
              height: theme.spacing(2),
              margin: theme.spacing(0.25),
            },
          }),
      },
    },
    MuiTab: {
      styleOverrides: {
        root: ({ theme }) =>
          theme.unstable_sx({
            p: 0,
            bt: 0,
            borderBottom: 1,
            width: '100%',
            backgroundColor: 'surface.main',
            color: 'surface.onVariant',
            '&:hover:not(.Mui-selected)': {
              backgroundColor: 'surface.container',
              color: 'surface.contrastText',
              opacity: '0.8'
            },
            '&.Mui-selected': {
              backgroundColor: 'surface.container',
              color: 'surface.contrastText',
            },
          }),
      },
    },
    MuiTabPanel: {
      styleOverrides: {
        root: ({ theme }) =>
          theme.unstable_sx({
            pt: 1,
            px: 0,
            pb: 0,
          }),
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: ({ theme }) =>
          theme.unstable_sx({
            '&.firstColumn': {
              backgroundColor: 'primary.container',
              color: 'primary.onContainer',
            },
            '& .MuiTableCell-root:last-child': {
              borderRight: 'none',
            },
            borderRightWidth: '1px',
            borderRightStyle: 'solid',
            borderRightColor: 'divider',
            padding: 1,
            minWidth: '150px',
            whiteSpace: 'nowrap',
          }),
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: ({ theme }) =>
          theme.unstable_sx({
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: 'divider',
            overflowX: 'auto',
            maxWidth: '100%',
          }),
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: ({ theme }) =>
          theme.unstable_sx({
            '&.MuiTableRow-head': {
              backgroundColor: 'primary.container',
              color: 'primary.onContainer',
            },
            backgroundColor: 'surface.main',
            color: 'surface.contrastText',
          }),
      },
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: ({ theme }) =>
          theme.unstable_sx({
            display: 'inline-grid',
            gridAutoFlow: 'column',
            gridAutoColumns: '1fr',
            backgroundColor: 'transparent',
            color: 'surface.contrastText',
            [`& .MuiToggleButtonGroup-grouped`]: {
              '&.Mui-disabled': {
                border: 0,
              },
            },
            [`& .MuiToggleButtonGroup-middleButton, & .MuiToggleButtonGroup-lastButton`]: {
              borderLeft: '1px solid',
              borderLeftColor: 'outline.main',
            },
          }),
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: ({ theme }) =>
          theme.unstable_sx({
            borderRadius: (theme.shape.borderRadius as number) * 2,
            border: '1px solid',
            borderColor: 'outline.main',
            backgroundColor: 'surface.main',
            '&.Mui-selected': {
              color: 'secondary.onContainer',
              backgroundColor: 'secondary.container',
            },
          }),
      },
    },
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          headline: 'h5',
          title: 'h6',
          h6dense: 'h6',
          label: 'p',
          pdense: 'p',
          iconbutton: 'p',
          displayLarge: 'h1',
          displayMedium: 'h2',
          displaySmall: 'h2',
          headlineLarge: 'h3',
          headlineMedium: 'h4',
          headlineSmall: 'h4',
          titleLarge: 'h5',
          titleMedium: 'h6',
          titleSmall: 'h6',
          brandLarge: 'h5',
          brandMedium: 'h6',
          brandSmall: 'h6',
          labelLarge: 'label',
          labelMedium: 'label',
          labelSmall: 'label',
          bodyLarge: 'p',
          bodyMedium: 'p',
          bodySmall: 'p',
        },
      },
      styleOverrides: {
        root: ({ theme }) =>
          theme.unstable_sx({
            '&.title': {
              color: 'surface.contrastText',
              variant: 'title',
            },
            // Semantic color classes for typography
            '&.emphasized-text': {
              color: 'primary.main',
              fontWeight: 500,
            },
            '&.secondary-text': {
              color: 'text.secondary',
            },
            '&.muted-text': {
              color: 'text.disabled',
            },
            '&.metadata-text': {
              color: 'text.secondary',
            },
            '&.label-text': {
              color: 'text.secondary',
            },
            '&.caption-text': {
              color: 'text.secondary',
            },
            '&.error-text': {
              color: 'error.main',
            },
            '&.cancelled-text': {
              color: 'error.main',
            },
            '&.success-text': {
              color: 'success.main',
            },
            '&.warning-text': {
              color: 'warning.main',
            },
          }),
        // Alias legacy MUI body1/body2 to MD3 scale for rendering consistency
        body1: ({ theme }) => ({ ...theme.typography.bodyLarge }),
        body2: ({ theme }) => ({ ...theme.typography.bodySmall }),
      },
    },
  },
  typography: {
    fontFamily: 'Inter, sans-serif',
    headline: {
      fontSize: '1.5rem',
      fontWeight: 500,
    },
    h6dense: {
      fontSize: '1rem',
      fontWeight: 800,
    },
    label: {
      fontSize: '.8rem',
      fontWeight: 200,
    },
    pdense: {
      fontSize: '.8rem',
      fontWeight: 300,
      textAlign: 'center',
    },
    iconbutton: {
      fontSize: '.8rem',
      fontWeight: 300,
    },
    displayLarge: {
      fontSize: '3.5625rem', // 57px
      fontWeight: 400,
      lineHeight: '4rem', // 64px
      letterSpacing: '-0.0156rem',
    },
    displayMedium: {
      fontSize: '2.8125rem', // 45px
      fontWeight: 400,
      lineHeight: '3.25rem', // 52px
    },
    displaySmall: {
      fontSize: '2.25rem', // 36px
      fontWeight: 400,
      lineHeight: '2.75rem', // 44px
    },
    headlineLarge: {
      fontSize: '2rem', // 32px
      fontWeight: 400,
      lineHeight: '2.5rem', // 40px
    },
    headlineMedium: {
      fontSize: '1.75rem', // 28px
      fontWeight: 400,
      lineHeight: '2.25rem', // 36px
    },
    headlineSmall: {
      fontSize: '1.5rem', // 24px
      fontWeight: 400,
      lineHeight: '2rem', // 32px
    },
    titleLarge: {
      fontSize: '1.375rem', // 22px
      fontWeight: 400,
      lineHeight: '1.75rem', // 28px
    },
    titleMedium: {
      fontSize: '1rem', // 16px
      fontWeight: 500,
      letterSpacing: '0.0094rem', // 0.15px
      lineHeight: '1.5rem', // 24px
    },
    titleSmall: {
      fontSize: '0.875rem', // 14px
      fontWeight: 500,
      letterSpacing: '0.0063rem', // 0.1px
      lineHeight: '1.25rem', // 20px
    },
    brandLarge: {
      color: 'surface.onBrand',
      fontFamily: 'Inter',
      fontSize: '4rem',
      fontStyle: 'normal',
      fontWeight: 400,
      lineHeight: '4.5rem', /* 112.5% */
      letterSpacing: '0.00625rem',
      textAlign: 'center',
    },
    brandMedium: {
      color: 'surface.onBrand',
      fontFamily: 'Inter',
      fontSize: '1.25rem',
      fontStyle: 'normal',
      fontWeight: 400,
      lineHeight: '1.8rem', /* 112.5% */
      letterSpacing: '0.00625rem',
      textAlign: 'center',
    },
    brandSmall: { 
      color: 'surface.onBrand',
      fontFamily: 'Inter',
      fontSize: '1rem',
      fontStyle: 'normal',
      fontWeight: 400,
      lineHeight: '1.338rem', /* 112.5% */
      letterSpacing: '0.00625rem',
      textAlign: 'center',
    },
    bodyLarge: {
      fontSize: '1rem', // 16px
      fontWeight: 400,
      letterSpacing: '0.031rem', // 0.5px
      lineHeight: '1.5rem', // 24px
    },
    bodyMedium: {
      fontSize: '0.875rem', // 14px
      fontWeight: 400,
      letterSpacing: '0.0156rem', // 0.25px
      lineHeight: '1.25rem', // 20px
    },
    bodySmall: {
      fontSize: '0.75rem', // 12px
      fontWeight: 400,
      letterSpacing: '0.025rem', // 0.4px
      lineHeight: '1rem', // 16px
    },
    labelLarge: {
      fontSize: '0.875rem', // 14px
      fontWeight: 500,
      letterSpacing: '0.0063rem', // 0.1px
      lineHeight: '1.25rem', // 20px
    },
    labelMedium: {
      fontSize: '0.75rem', // 12px
      fontWeight: 500,
      letterSpacing: '0.031rem', // 0.5px
      lineHeight: '1rem', // 16px
    },
    labelSmall: {
      fontSize: '0.6875rem', // 11px
      fontWeight: 500,
      letterSpacing: '0.031rem', // 0.5px
      lineHeight: '1rem', // 16px
    },
  },
  colorSchemes: {
    dark: {
      palette: {
        primary: {                            // em
          main: '#5A7951',                  // em     
          light: alpha('#5A7951', 0.5),     // em                  
          dark: alpha('#5A7951', 0.9),      // em                 
          contrastText: '#FFFFFF',          // em             
          container: '#2A3323',             // em          
          onContainer: '#DCE8D0',           // em            
          fixed: '#DCE8D0',                 // em      
          onFixed: '#002106',               // em        
          fixedDim: '#B7CC9B',              // em         
          onFixedVariant: '#FFFFFF',        // em
          inverse: '#EAEAD0',               // em
        },
        secondary: {
          main: '#B2B29F',                  // em
          light: alpha('#B2B29F', 0.5),     // em
          dark: alpha('#B2B29F', 0.9),      // em
          contrastText: '#FFFFFF',          // em
          container: '#3E3E30',             // em
          onContainer: '#E9E9D7',           // em
          fixed: '#E9E9D7',                 // em
          onFixed: '#1C1C0E',               // em
          fixedDim: '#CDCDC0',              // em
          onFixedVariant: '#1C1C03',        // em
        },
        tertiary: {
          main: '#9FB2B1',                  // em
          light: alpha('#9FB2B1', 0.5),     // em
          dark: alpha('#9FB2B1', 0.9),      // em
          contrastText: '#FFFFFF',          // em
          container: '#2F4443',             // em
          onContainer: '#FFFFFF',           // em
          fixed: '#9FB2B1',                 // em
          onFixed: '#FFFFFF',               // em
          fixedDim: alpha('#9FB2B1', 0.2),  // em
          onFixedVariant: '#FFFFFF',        // em
        },
        error: {
          main: '#D14343',
          contrastText: '#5A1A1A',
        },
        success: {
          main: '#66BB6A',
          contrastText: '#2E5D2C',
        },
        warning: {
          main: '#F4B740',
          contrastText: '#3E2A00',
        },
        test: {
          main: '#FFD700',
          contrastText: '#694E00',
          container: '#936B00',
          onContainer: '#FFE999',
          variant: '#7B5B00',
          onVariant: '#FFE070',
          dim: '#4C3900',
          bright: '#FFEB3B',
        },
        emulator: {
          main: '#FF6F00',
          contrastText: '#FFFFFF',
          container: '#E65100',
          onContainer: '#FFF3E0',
        },
        god: {
          main: '#B8860B',
          contrastText: '#FFFFFF',
          container: '#6B5010',
          onContainer: '#FFE999',
        },
        caution: {
          main: '#D4A017',
          contrastText: '#1C1400',
        },
        background: {
          default: '#212529',
          //alt: "#2C2F33"
        },
        surface: {
          main: '#2C2F33',
          light: alpha('#2C2F33', 0.5),     // em                  
          dark: alpha('#2C2F33', 0.9),      // em 
          inverse: '#E2E2E6',
          onInverse: '#8F909A',
          contrastText: '#FFFFFF',
          variant: '#E2E2E6',
          onVariant: '#C5C6D0',
          dim: '#0E1113',
          onBrand: '#FFFFFF',
          bright: '#373A3E',
          containerLow: '#272A2E',
          container: '#2C2F33',
          containerHigh: '#373A3E',
          onContainer: '#FFFFFF'
          // strong: "#FFFFFF",
          //onSurface: "#FFFFFF"
        },
        outline: {
          main: '#968E98',
          variant: '#D4D4D4',
        },
        divider: '#4B454D', // Same as OutlineVariant in MD3
      },
    },
    light: {
      palette: {
        primary: {
          main: '#4C6744',
          light: alpha('#4C6744', 0.5),     // em
          dark: alpha('#4C6744', 0.9),      // em
          contrastText: '#FFFFFF',
          container: '#CEEBC2',
          onContainer: '#0B2007',
          fixed: '#CEEBC2',
          onFixed: '#0B2007',
          fixedDim: '#A3BFA0',
          onFixedVariant: '#3A4F34',
          inverse: '#B3CFA7',
        },
        secondary: {
          main: '#849A81',
          light: alpha('#849A81', 0.5),     // em
          dark: alpha('#849A81', 0.9),      // em
          contrastText: '#1A1C19',
          container: '#DCE8D7',
          onContainer: '#1A1C19',
          fixed: '#849A81',
          onFixed: '#1A1C19',
          fixedDim: '#C0CCBC',
          onFixedVariant: '#424F40',
          inverse: '#C0CCBC'
        },
        tertiary: {
          main: '#6C8583',
          contrastText: '#FFFFFF',
          container: '#D1EAE6',
          onContainer: '#05201D',
          fixed: '#6C8583',
          onFixed: '#FFFFFF',
          fixedDim: '#B5CECA',
          onFixedVariant: '#3F5350',
          inverse: '#B5CECA'
        },
        error: {
          main: '#B94747',
          contrastText: '#FCE8E8',
          container: '#FCE8E8',
          onContainer: '#410002',
        },
        success: {
          main: '#3F7D3B',
          contrastText: '#DFF0DD',
        },
        warning: {
          main: '#F4B740',
          contrastText: '#3E2A00',
        },
        test: {
          main: '#B04159',
          contrastText: '#FFFFFF',
          container: '#F3E6E8',
          onContainer: '#410011',
          variant: '#B04159', // TODO: Color not provided by Em
          onVariant: '#F3E6E8', // TODO: Color not provided by Em
          dim: '#410011', // TODO: Color not provided by Em
          bright: '#F3E6E8', // TODO: Color not provided by Em
        },
        emulator: {
          main: '#FF9100',
          contrastText: '#000000',
          container: '#FFE0B2',
          onContainer: '#E65100',
        },
        god: {
          main: '#FFD700',
          contrastText: '#000000',
          container: '#FFF8DC',
          onContainer: '#8B6914',
        },
        caution: {
          main: '#CA8A04',
          contrastText: '#FFFFFF',
        },
        background: {
          default: '#fCfBf6',
          // alt: '#F9F8F0',
        },
        surface: {
          main: '#fcfcfc',
          light: alpha('#FCFCFC', 0.5),     // em                  
          dark: alpha('#FCFCFC', 0.9),      // em 
          inverse: '#2F312D',
          onInverse: '#F1F1EB',
          contrastText: '#1A1C18',
          variant: '#EAEAD0',
          onVariant: '#444941',
          dim: '#D9D9D9',
          onBrand: '#424242',
          bright: '#FCFCFC',
          containerLow: '#F3F4EF',
          container: '#EDEEE9',
          containerHigh: '#E2E3DC',
          onContainer: '#1A1C18'
          //strong: '#3c3c3c',
          //onSurface: '#212529',
        },
        outline: {
          main: '#E5E5E1',
          variant: '#B0B0B0',
        },
        divider: '#E5E5E1',
      },
    },
  },
  spacing: (factor: number) => `${0.5 * factor}rem`,
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
});

export default mui6Theme;

/*
{
    "description": "TYPE: CUSTOM\nMaterial Theme Builder export 2024-10-06 10:44:37",
    "seed": "#803DB8",
    "coreColors": {
        "primary": "#803DB8"
    },
    "extendedColors": [],
    "schemes": {
        "light": {
            "primary": "#6923A1",
            "surfaceTint": "#7F3CB7",
            "onPrimary": "#FFFFFF",
            "primaryContainer": "#904DC8",
            "onPrimaryContainer": "#FFFFFF",
            "secondary": "#6E5384",
            "onSecondary": "#FFFFFF",
            "secondaryContainer": "#EACCFF",
            "onSecondaryContainer": "#4F3664",
            "tertiary": "#89165C",
            "onTertiary": "#FFFFFF",
            "tertiaryContainer": "#B73F82",
            "onTertiaryContainer": "#FFFFFF",
            "error": "#BA1A1A",
            "onError": "#FFFFFF",
            "errorContainer": "#FFDAD6",
            "onErrorContainer": "#410002",
            "background": "#FFF7FD",
            "onBackground": "#1F1A22",
            "surface": "#FFF7FD",
            "onSurface": "#1F1A22",
            "surfaceVariant": "#ECDEF0",
            "onSurfaceVariant": "#4C4452",
            "outline": "#7E7383",
            "outlineVariant": "#CFC2D4",
            "shadow": "#000000",
            "scrim": "#000000",
            "inverseSurface": "#342E37",
            "inverseOnSurface": "#F8EEF9",
            "inversePrimary": "#DFB7FF",
            "primaryFixed": "#F1DAFF",
            "onPrimaryFixed": "#2D004F",
            "primaryFixedDim": "#DFB7FF",
            "onPrimaryFixedVariant": "#661F9E",
            "secondaryFixed": "#F1DAFF",
            "onSecondaryFixed": "#280F3C",
            "secondaryFixedDim": "#DBBAF2",
            "onSecondaryFixedVariant": "#553C6B",
            "tertiaryFixed": "#FFD8E7",
            "onTertiaryFixed": "#3D0026",
            "tertiaryFixedDim": "#FFAFD3",
            "onTertiaryFixedVariant": "#86135A",
            "surfaceDim": "#E1D7E2",
            "surfaceBright": "#FFF7FD",
            "surfaceContainerLowest": "#FFFFFF",
            "surfaceContainerLow": "#FBF0FC",
            "surfaceContainer": "#F5EBF6",
            "surfaceContainerHigh": "#EFE5F0",
            "surfaceContainerHighest": "#E9DFEA"
        },
        "light-medium-contrast": {
            "primary": "#621999",
            "surfaceTint": "#7F3CB7",
            "onPrimary": "#FFFFFF",
            "primaryContainer": "#904DC8",
            "onPrimaryContainer": "#FFFFFF",
            "secondary": "#513866",
            "onSecondary": "#FFFFFF",
            "secondaryContainer": "#85699C",
            "onSecondaryContainer": "#FFFFFF",
            "tertiary": "#810C56",
            "onTertiary": "#FFFFFF",
            "tertiaryContainer": "#B73F82",
            "onTertiaryContainer": "#FFFFFF",
            "error": "#8C0009",
            "onError": "#FFFFFF",
            "errorContainer": "#DA342E",
            "onErrorContainer": "#FFFFFF",
            "background": "#FFF7FD",
            "onBackground": "#1F1A22",
            "surface": "#FFF7FD",
            "onSurface": "#1F1A22",
            "surfaceVariant": "#ECDEF0",
            "onSurfaceVariant": "#48404E",
            "outline": "#655C6B",
            "outlineVariant": "#817787",
            "shadow": "#000000",
            "scrim": "#000000",
            "inverseSurface": "#342E37",
            "inverseOnSurface": "#F8EEF9",
            "inversePrimary": "#DFB7FF",
            "primaryFixed": "#9755D0",
            "onPrimaryFixed": "#FFFFFF",
            "primaryFixedDim": "#7D3AB5",
            "onPrimaryFixedVariant": "#FFFFFF",
            "secondaryFixed": "#85699C",
            "onSecondaryFixed": "#FFFFFF",
            "secondaryFixedDim": "#6C5181",
            "onSecondaryFixedVariant": "#FFFFFF",
            "tertiaryFixed": "#C1478A",
            "onTertiaryFixed": "#FFFFFF",
            "tertiaryFixedDim": "#A12D70",
            "onTertiaryFixedVariant": "#FFFFFF",
            "surfaceDim": "#E1D7E2",
            "surfaceBright": "#FFF7FD",
            "surfaceContainerLowest": "#FFFFFF",
            "surfaceContainerLow": "#FBF0FC",
            "surfaceContainer": "#F5EBF6",
            "surfaceContainerHigh": "#EFE5F0",
            "surfaceContainerHighest": "#E9DFEA"
        },
        "light-high-contrast": {
            "primary": "#37005E",
            "surfaceTint": "#7F3CB7",
            "onPrimary": "#FFFFFF",
            "primaryContainer": "#621999",
            "onPrimaryContainer": "#FFFFFF",
            "secondary": "#2F1743",
            "onSecondary": "#FFFFFF",
            "secondaryContainer": "#513866",
            "onSecondaryContainer": "#FFFFFF",
            "tertiary": "#49002E",
            "onTertiary": "#FFFFFF",
            "tertiaryContainer": "#810C56",
            "onTertiaryContainer": "#FFFFFF",
            "error": "#4E0002",
            "onError": "#FFFFFF",
            "errorContainer": "#8C0009",
            "onErrorContainer": "#FFFFFF",
            "background": "#FFF7FD",
            "onBackground": "#1F1A22",
            "surface": "#FFF7FD",
            "onSurface": "#000000",
            "surfaceVariant": "#ECDEF0",
            "onSurfaceVariant": "#29212E",
            "outline": "#48404E",
            "outlineVariant": "#48404E",
            "shadow": "#000000",
            "scrim": "#000000",
            "inverseSurface": "#342E37",
            "inverseOnSurface": "#FFFFFF",
            "inversePrimary": "#F7E6FF",
            "primaryFixed": "#621999",
            "onPrimaryFixed": "#FFFFFF",
            "primaryFixedDim": "#460075",
            "onPrimaryFixedVariant": "#FFFFFF",
            "secondaryFixed": "#513866",
            "onSecondaryFixed": "#FFFFFF",
            "secondaryFixedDim": "#3A224F",
            "onSecondaryFixedVariant": "#FFFFFF",
            "tertiaryFixed": "#810C56",
            "onTertiaryFixed": "#FFFFFF",
            "tertiaryFixedDim": "#5C003B",
            "onTertiaryFixedVariant": "#FFFFFF",
            "surfaceDim": "#E1D7E2",
            "surfaceBright": "#FFF7FD",
            "surfaceContainerLowest": "#FFFFFF",
            "surfaceContainerLow": "#FBF0FC",
            "surfaceContainer": "#F5EBF6",
            "surfaceContainerHigh": "#EFE5F0",
            "surfaceContainerHighest": "#E9DFEA"
        },
        "dark": {
            "primary": "#DFB7FF",
            "surfaceTint": "#DFB7FF",
            "onPrimary": "#4B007D",
            "primaryContainer": "#7632AE",
            "onPrimaryContainer": "#FFF8FC",
            "secondary": "#DBBAF2",
            "onSecondary": "#3E2553",
            "secondaryContainer": "#4C3361",
            "onSecondaryContainer": "#E6C5FD",
            "tertiary": "#FFAFD3",
            "onTertiary": "#620040",
            "tertiaryContainer": "#9A266A",
            "onTertiaryContainer": "#FFF9F9",
            "error": "#FFB4AB",
            "onError": "#690005",
            "errorContainer": "#93000A",
            "onErrorContainer": "#FFDAD6",
            "background": "#161219",
            "onBackground": "#E9DFEA",
            "surface": "#161219",
            "onSurface": "#E9DFEA",
            "surfaceVariant": "#4C4452",
            "onSurfaceVariant": "#CFC2D4",
            "outline": "#988D9D",
            "outlineVariant": "#4C4452",
            "shadow": "#000000",
            "scrim": "#000000",
            "inverseSurface": "#E9DFEA",
            "inverseOnSurface": "#342E37",
            "inversePrimary": "#7F3CB7",
            "primaryFixed": "#F1DAFF",
            "onPrimaryFixed": "#2D004F",
            "primaryFixedDim": "#DFB7FF",
            "onPrimaryFixedVariant": "#661F9E",
            "secondaryFixed": "#F1DAFF",
            "onSecondaryFixed": "#280F3C",
            "secondaryFixedDim": "#DBBAF2",
            "onSecondaryFixedVariant": "#553C6B",
            "tertiaryFixed": "#FFD8E7",
            "onTertiaryFixed": "#3D0026",
            "tertiaryFixedDim": "#FFAFD3",
            "onTertiaryFixedVariant": "#86135A",
            "surfaceDim": "#161219",
            "surfaceBright": "#3D3740",
            "surfaceContainerLowest": "#110D14",
            "surfaceContainerLow": "#1F1A22",
            "surfaceContainer": "#231E26",
            "surfaceContainerHigh": "#2D2830",
            "surfaceContainerHighest": "#38333B"
        },
        "dark-medium-contrast": {
            "primary": "#E2BCFF",
            "surfaceTint": "#DFB7FF",
            "onPrimary": "#260043",
            "primaryContainer": "#B572EF",
            "onPrimaryContainer": "#000000",
            "secondary": "#DFBFF6",
            "onSecondary": "#220937",
            "secondaryContainer": "#A385B9",
            "onSecondaryContainer": "#000000",
            "tertiary": "#FFB6D6",
            "onTertiary": "#33001F",
            "tertiaryContainer": "#E363A7",
            "onTertiaryContainer": "#000000",
            "error": "#FFBAB1",
            "onError": "#370001",
            "errorContainer": "#FF5449",
            "onErrorContainer": "#000000",
            "background": "#161219",
            "onBackground": "#E9DFEA",
            "surface": "#161219",
            "onSurface": "#FFF9FB",
            "surfaceVariant": "#4C4452",
            "onSurfaceVariant": "#D3C6D8",
            "outline": "#AB9FB0",
            "outlineVariant": "#8A7F8F",
            "shadow": "#000000",
            "scrim": "#000000",
            "inverseSurface": "#E9DFEA",
            "inverseOnSurface": "#2D2830",
            "inversePrimary": "#67209F",
            "primaryFixed": "#F1DAFF",
            "onPrimaryFixed": "#1E0037",
            "primaryFixedDim": "#DFB7FF",
            "onPrimaryFixedVariant": "#53008A",
            "secondaryFixed": "#F1DAFF",
            "onSecondaryFixed": "#1D0431",
            "secondaryFixedDim": "#DBBAF2",
            "onSecondaryFixedVariant": "#442B59",
            "tertiaryFixed": "#FFD8E7",
            "onTertiaryFixed": "#2A0019",
            "tertiaryFixedDim": "#FFAFD3",
            "onTertiaryFixedVariant": "#6D0047",
            "surfaceDim": "#161219",
            "surfaceBright": "#3D3740",
            "surfaceContainerLowest": "#110D14",
            "surfaceContainerLow": "#1F1A22",
            "surfaceContainer": "#231E26",
            "surfaceContainerHigh": "#2D2830",
            "surfaceContainerHighest": "#38333B"
        },
        "dark-high-contrast": {
            "primary": "#FFF9FB",
            "surfaceTint": "#DFB7FF",
            "onPrimary": "#000000",
            "primaryContainer": "#E2BCFF",
            "onPrimaryContainer": "#000000",
            "secondary": "#FFF9FB",
            "onSecondary": "#000000",
            "secondaryContainer": "#DFBFF6",
            "onSecondaryContainer": "#000000",
            "tertiary": "#FFF9F9",
            "onTertiary": "#000000",
            "tertiaryContainer": "#FFB6D6",
            "onTertiaryContainer": "#000000",
            "error": "#FFF9F9",
            "onError": "#000000",
            "errorContainer": "#FFBAB1",
            "onErrorContainer": "#000000",
            "background": "#161219",
            "onBackground": "#E9DFEA",
            "surface": "#161219",
            "onSurface": "#FFFFFF",
            "surfaceVariant": "#4C4452",
            "onSurfaceVariant": "#FFF9FB",
            "outline": "#D3C6D8",
            "outlineVariant": "#D3C6D8",
            "shadow": "#000000",
            "scrim": "#000000",
            "inverseSurface": "#E9DFEA",
            "inverseOnSurface": "#000000",
            "inversePrimary": "#42006F",
            "primaryFixed": "#F4E0FF",
            "onPrimaryFixed": "#000000",
            "primaryFixedDim": "#E2BCFF",
            "onPrimaryFixedVariant": "#260043",
            "secondaryFixed": "#F4E0FF",
            "onSecondaryFixed": "#000000",
            "secondaryFixedDim": "#DFBFF6",
            "onSecondaryFixedVariant": "#220937",
            "tertiaryFixed": "#FFDEEA",
            "onTertiaryFixed": "#000000",
            "tertiaryFixedDim": "#FFB6D6",
            "onTertiaryFixedVariant": "#33001F",
            "surfaceDim": "#161219",
            "surfaceBright": "#3D3740",
            "surfaceContainerLowest": "#110D14",
            "surfaceContainerLow": "#1F1A22",
            "surfaceContainer": "#231E26",
            "surfaceContainerHigh": "#2D2830",
            "surfaceContainerHighest": "#38333B"
        }
    },
    "palettes": {
        "primary": {
            "0": "#000000",
            "5": "#1E0037",
            "10": "#2D004F",
            "15": "#3C0066",
            "20": "#4B007D",
            "25": "#590A91",
            "30": "#661F9E",
            "35": "#722EAA",
            "40": "#7F3CB7",
            "50": "#9A57D3",
            "60": "#B572EF",
            "70": "#CD92FF",
            "80": "#DFB7FF",
            "90": "#F1DAFF",
            "95": "#FAECFF",
            "98": "#FFF7FD",
            "99": "#FFFBFF",
            "100": "#FFFFFF"
        },
        "secondary": {
            "0": "#000000",
            "5": "#180B24",
            "10": "#23162F",
            "15": "#2E203A",
            "20": "#392A45",
            "25": "#453551",
            "30": "#50415D",
            "35": "#5D4C69",
            "40": "#695875",
            "50": "#82708F",
            "60": "#9D8AAA",
            "70": "#B8A4C5",
            "80": "#D4BFE1",
            "90": "#F1DBFE",
            "95": "#FAECFF",
            "98": "#FFF7FD",
            "99": "#FFFBFF",
            "100": "#FFFFFF"
        },
        "tertiary": {
            "0": "#000000",
            "5": "#2A0209",
            "10": "#380B13",
            "15": "#46151D",
            "20": "#532027",
            "25": "#612A31",
            "30": "#6E353C",
            "35": "#7C4148",
            "40": "#8A4C53",
            "50": "#A7646B",
            "60": "#C57D84",
            "70": "#E3979E",
            "80": "#FFB2B9",
            "90": "#FFDADC",
            "95": "#FFECED",
            "98": "#FFF8F7",
            "99": "#FFFBFF",
            "100": "#FFFFFF"
        },
        "neutral": {
            "0": "#000000",
            "5": "#121013",
            "10": "#1D1B1E",
            "15": "#282528",
            "20": "#322F33",
            "25": "#3E3A3E",
            "30": "#494649",
            "35": "#555155",
            "40": "#615D61",
            "50": "#7A767A",
            "60": "#948F93",
            "70": "#AFAAAE",
            "80": "#CBC5C9",
            "90": "#E7E1E5",
            "95": "#F6EFF3",
            "98": "#FEF7FC",
            "99": "#FFFBFF",
            "100": "#FFFFFF"
        },
        "neutral-variant": {
            "0": "#000000",
            "5": "#140F17",
            "10": "#1E1A21",
            "15": "#29242C",
            "20": "#342E37",
            "25": "#3F3942",
            "30": "#4B454D",
            "35": "#575059",
            "40": "#635C65",
            "50": "#7C757E",
            "60": "#968E98",
            "70": "#B1A8B3",
            "80": "#CDC4CE",
            "90": "#E9DFEA",
            "95": "#F8EEF9",
            "98": "#FFF7FD",
            "99": "#FFFBFF",
            "100": "#FFFFFF"
        }
    }
}
*/
